import { useState, useEffect, useRef, useCallback } from 'react'
import { Room, ConnectionState, RoomEvent, DisconnectReason } from 'livekit-client'
import { partyApi } from '../lib/api'

const MAX_RETRIES = 3
const MAX_RECONNECTS = 5 // stop auto-reconnecting after this many drop cycles
const BASE_BACKOFF_MS = 2000 // 2s, 4s, 8s

interface UseLiveKitOptions {
  partyCode: string
  enabled: boolean
}

interface UseLiveKitResult {
  room: Room | null
  connectionState: ConnectionState
  error: string | null
  reconnect: () => void
}

const disconnectReasonName = (reason?: DisconnectReason): string => {
  if (reason === undefined) return 'UNKNOWN'
  const names: Record<number, string> = {
    [DisconnectReason.UNKNOWN_REASON]: 'UNKNOWN_REASON',
    [DisconnectReason.CLIENT_INITIATED]: 'CLIENT_INITIATED',
    [DisconnectReason.DUPLICATE_IDENTITY]: 'DUPLICATE_IDENTITY',
    [DisconnectReason.SERVER_SHUTDOWN]: 'SERVER_SHUTDOWN',
    [DisconnectReason.PARTICIPANT_REMOVED]: 'PARTICIPANT_REMOVED',
    [DisconnectReason.ROOM_DELETED]: 'ROOM_DELETED',
    [DisconnectReason.STATE_MISMATCH]: 'STATE_MISMATCH',
    [DisconnectReason.JOIN_FAILURE]: 'JOIN_FAILURE',
  }
  return names[reason] ?? `REASON_${reason}`
}

export function useLiveKit({ partyCode, enabled }: UseLiveKitOptions): UseLiveKitResult {
  const [room, setRoom] = useState<Room | null>(null)
  const [connectionState, setConnectionState] = useState<ConnectionState>(ConnectionState.Disconnected)
  const [error, setError] = useState<string | null>(null)
  const roomRef = useRef<Room | null>(null)
  const generationRef = useRef(0)
  const retryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const reconnectCountRef = useRef(0)

  const cleanupRoom = useCallback(() => {
    if (retryTimerRef.current) {
      clearTimeout(retryTimerRef.current)
      retryTimerRef.current = null
    }
    if (roomRef.current) {
      roomRef.current.removeAllListeners()
      roomRef.current.disconnect()
      roomRef.current = null
    }
    setRoom(null)
    setConnectionState(ConnectionState.Disconnected)
  }, [])

  const connectWithRetry = useCallback(async (generation: number, attempt: number = 0) => {
    if (generationRef.current !== generation) return

    setError(null)
    setConnectionState(ConnectionState.Connecting)

    try {
      console.log('[LiveKit] connect attempt=%d gen=%d, calling partyApi.join(%s)', attempt, generation, partyCode)
      const joinResp = await partyApi.join(partyCode, 'Admin Dashboard')
      console.log('[LiveKit] join response received, livekit.url=%s', joinResp.livekit.url)
      if (generationRef.current !== generation) return

      const { url, token } = joinResp.livekit

      // Create room with minimal options to avoid media negotiation issues
      const newRoom = new Room({
        adaptiveStream: false,
        dynacast: false,
      })

      // Log disconnect reasons for debugging
      newRoom.on(RoomEvent.Disconnected, (reason?: DisconnectReason) => {
        console.log('[LiveKit] RoomEvent.Disconnected reason=%s (gen=%d, current=%d)',
          disconnectReasonName(reason), generation, generationRef.current)
      })

      // Track connection state changes for reconnection logic
      newRoom.on(RoomEvent.ConnectionStateChanged, (state: ConnectionState) => {
        console.log('[LiveKit] ConnectionStateChanged: %s (gen=%d, current=%d)', state, generation, generationRef.current)
        if (generationRef.current !== generation) return
        setConnectionState(state)

        if (state === ConnectionState.Disconnected && roomRef.current === newRoom) {
          // Clean up the disconnected room's listeners
          newRoom.removeAllListeners()
          roomRef.current = null
          setRoom(null)
          reconnectCountRef.current++
          if (reconnectCountRef.current > MAX_RECONNECTS) {
            console.log('[LiveKit] max reconnects (%d) exhausted, giving up', MAX_RECONNECTS)
            setError('Connection keeps dropping. Click Reconnect to try again.')
            return
          }
          const delay = BASE_BACKOFF_MS * Math.min(reconnectCountRef.current, 4)
          console.log('[LiveKit] connection lost, reconnect %d/%d in %dms', reconnectCountRef.current, MAX_RECONNECTS, delay)
          retryTimerRef.current = setTimeout(() => {
            if (generationRef.current === generation) {
              connectWithRetry(generation, 0)
            }
          }, delay)
        }
      })

      console.log('[LiveKit] calling room.connect(%s)', url)
      await newRoom.connect(url, token)
      console.log('[LiveKit] room.connect() resolved, state=%s', newRoom.state)
      if (generationRef.current !== generation) {
        newRoom.removeAllListeners()
        newRoom.disconnect()
        return
      }

      roomRef.current = newRoom
      setRoom(newRoom)
      setConnectionState(ConnectionState.Connected)
      setError(null)
      reconnectCountRef.current = 0 // Reset on successful connect
      console.log('[LiveKit] connected successfully')
    } catch (err) {
      console.error('[LiveKit] connect error (attempt %d):', attempt, err)
      if (generationRef.current !== generation) return

      if (attempt < MAX_RETRIES) {
        const delay = BASE_BACKOFF_MS * Math.pow(2, attempt)
        console.log('[LiveKit] retrying in %dms (attempt %d/%d)', delay, attempt + 1, MAX_RETRIES)
        setConnectionState(ConnectionState.Reconnecting)
        setError(null)
        retryTimerRef.current = setTimeout(() => {
          if (generationRef.current === generation) {
            connectWithRetry(generation, attempt + 1)
          }
        }, delay)
      } else {
        setError(err instanceof Error ? err.message : 'Failed to connect to LiveKit')
        setConnectionState(ConnectionState.Disconnected)
      }
    }
  }, [partyCode])

  useEffect(() => {
    console.log('[LiveKit] effect run: enabled=%s partyCode=%s', enabled, partyCode)
    if (!enabled || !partyCode) {
      cleanupRoom()
      return
    }

    const generation = ++generationRef.current
    reconnectCountRef.current = 0
    console.log('[LiveKit] starting connect, generation=%d', generation)
    connectWithRetry(generation, 0)

    return () => {
      console.log('[LiveKit] cleanup, bumping generation from %d', generationRef.current)
      generationRef.current++
      cleanupRoom()
    }
  }, [enabled, partyCode, connectWithRetry, cleanupRoom])

  // Manual reconnect — bumps generation (cancels in-flight) and starts fresh
  const reconnect = useCallback(() => {
    if (!enabled || !partyCode) return
    console.log('[LiveKit] manual reconnect requested')
    reconnectCountRef.current = 0
    cleanupRoom()
    const generation = ++generationRef.current
    connectWithRetry(generation, 0)
  }, [enabled, partyCode, connectWithRetry, cleanupRoom])

  return { room, connectionState, error, reconnect }
}
